import { Group } from '@/components/_common';
import ActionIcon from '@/components/_common/ActionIcon';
import StatusTag from '@/components/_common/StatusTag';
import { useAccountPayableReturnListInstallments } from '@/core/domain/financial/AccountPayable/AccountPayable.hooks';
import {
  AccountPayableSituationColor,
  InstallmentSituation,
  InstallmentSituationType,
} from '@/core/domain/financial/AccountPayable/AccountPayable.types';
import { useAccountReceivableDetail } from '@/core/domain/financial/AccountReceivable/AccountReceivable.hooks';
import {
  AccountReceivableSituation,
  AccountReceivableSituationColor,
  AccountRecivableSituationType,
} from '@/core/domain/financial/AccountReceivable/AccountReceivable.types';
import { Descriptions, Popover } from 'antd';
import { useMemo, useState } from 'react';
import { File } from 'react-feather';
import { Link } from 'react-router-dom';

interface Props {
  accountId: number | null;
  installmentId: number | null;
  type: 'payable' | 'receivable';
}

export default function MigrationInstallmentBinded(props: Props) {
  const [opened, setOpen] = useState(false);

  const accountReceivableDetail = useAccountReceivableDetail(
    props.accountId || undefined,
    opened && props.type === 'receivable',
  );

  const accountPayableDetail = useAccountPayableReturnListInstallments(
    (props.accountId || undefined) as number,
    opened && props.type === 'payable',
  );

  const isLoading =
    accountReceivableDetail.isLoading || accountPayableDetail.isLoading;

  const installment = useMemo(() => {
    if (props.type === 'payable') {
      const data = accountPayableDetail.data?.installmentPayables.find(
        (installment) => installment.id === props.installmentId,
      );

      return { ...data, customer: data?.credit };
    }

    const data = accountReceivableDetail.data?.installmentReceivables.find(
      (installment) => installment.id === props.installmentId,
    );

    return { ...data, customer: `` };
  }, [
    props.type,
    props.installmentId,
    accountPayableDetail.data,
    accountReceivableDetail.data,
  ]);

  const account = useMemo(() => {
    if (props.type === 'payable') {
      return accountPayableDetail.data;
    }
    return accountReceivableDetail.data;
  }, [props.type, accountPayableDetail.data, accountReceivableDetail.data]);

  const situation = useMemo(() => {
    if (props.type === 'receivable') {
      const installmentSituation =
        installment?.situation as AccountReceivableSituation;

      if (installmentSituation) {
        return {
          label: AccountRecivableSituationType[installmentSituation],
          color: AccountReceivableSituationColor[installmentSituation],
        };
      }

      return null;
    }

    const installmentSituation = installment?.situation as InstallmentSituation;

    if (installmentSituation) {
      return {
        label: InstallmentSituationType[installmentSituation],
        color: AccountPayableSituationColor[installmentSituation],
      };
    }

    return null;
  }, [installment]);

  const hrefReceiver = `/app/financial/billToReceiver/releaseBillToReceiver/${props.accountId}/installment/${props.installmentId}/general`;
  const hrefPayable = `/app/financial/billToPay/releaseBillToPay/detail/accountPayableId/${props.accountId}/installmentPayableId/${props.installmentId}/general`;

  const href = props.type === `payable` ? hrefPayable : hrefReceiver;

  if (!props.installmentId || !props.accountId) return null;

  return (
    <Popover
      title={
        !isLoading &&
        !!installment && (
          <Group gap={10}>
            <StatusTag color={situation?.color} label={situation?.label} />

            <strong>{`${account?.code}/${installment.installmentNumber}`}</strong>
          </Group>
        )
      }
      content={
        <>
          {isLoading && 'Carregando...'}
          {!isLoading && installment && (
            <>
              <Descriptions
                layout="vertical"
                column={1}
                style={{ maxWidth: 300, marginBottom: 16 }}
              >
                <Descriptions.Item
                  style={{ padding: 0 }}
                  label="Descrição"
                  labelStyle={{ fontWeight: 600 }}
                >
                  {installment.description}
                </Descriptions.Item>
              </Descriptions>

              <Link style={{ fontWeight: 600 }} to={href}>
                Acessar parcela
              </Link>
            </>
          )}
        </>
      }
      onOpenChange={setOpen}
    >
      <a href={href}>
        <ActionIcon style={{ pointerEvents: 'none' }}>
          <File style={{ flexShrink: 0 }} size={18} />
        </ActionIcon>
      </a>
    </Popover>
  );
}
